import React, { useState } from 'react';
import { MonthlyDates, ImportantDatesState, CalendarEvent, PeriodKey, emptyDatesState } from './types';
import { Calendar, CheckCircle2, AlertCircle, ChevronDown, CircleDot } from 'lucide-react';
import { motion } from 'framer-motion';
import { addMonths, format, parse, startOfMonth } from 'date-fns';
interface AdminEntryProps {
  periods: MonthlyDates;
  onSave: (periods: MonthlyDates, events: CalendarEvent[]) => void;
}
const MILESTONE_TITLES: Record<'primaryRefi' | 'secondInvestPurch', Record<'initialLE' | 'signCD' | 'close', string>> = {
  primaryRefi: {
    initialLE: 'Last Day to Send Out Initial LE',
    signCD: 'Last Day to Get CD Signed',
    close: 'Last Day to Close'
  },
  secondInvestPurch: {
    initialLE: 'Last Day to Send Out Initial LE',
    signCD: 'Last Day to Get CD Signed',
    close: 'Last Day to Close'
  }
};
// Build the next 18 months (including the current month) as selectable periods.
function buildPeriodOptions(): {
  key: PeriodKey;
  label: string;
}[] {
  const start = startOfMonth(new Date());
  return Array.from({
    length: 18
  }, (_, i) => {
    const d = addMonths(start, i);
    return {
      key: format(d, 'yyyy-MM'),
      label: format(d, 'MMMM yyyy')
    };
  });
}
function periodLabel(key: PeriodKey): string {
  try {
    return format(parse(key, 'yyyy-MM', new Date()), 'MMMM yyyy');
  } catch {
    return key;
  }
}
function hasDates(state: ImportantDatesState): boolean {
  return Object.values(state.primaryRefi).some(d => d) || Object.values(state.secondInvestPurch).some(d => d);
}
export function AdminEntry({
  periods,
  onSave
}: AdminEntryProps) {
  const periodOptions = buildPeriodOptions();
  const [drafts, setDrafts] = useState<MonthlyDates>(periods);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>(periodOptions[0].key);
  const [isSaved, setIsSaved] = useState(false);
  const [createdEvents, setCreatedEvents] = useState<CalendarEvent[]>([]);
  const currentState: ImportantDatesState = drafts[selectedPeriod] ?? emptyDatesState;
  const handleDateChange = (track: keyof ImportantDatesState, field: keyof ImportantDatesState['primaryRefi'], value: string) => {
    setDrafts(prev => {
      const base = prev[selectedPeriod] ?? emptyDatesState;
      return {
        ...prev,
        [selectedPeriod]: {
          ...base,
          [track]: {
            ...base[track],
            [field]: value
          }
        }
      };
    });
    setIsSaved(false);
  };
  // Months that currently have at least one date entered.
  const preppedMonths = Object.entries(drafts).filter(([, state]) => hasDates(state)).map(([key]) => key).sort();
  const totalDateCount = preppedMonths.reduce((acc, key) => {
    const s = drafts[key];
    return acc + Object.values(s.primaryRefi).filter(d => d).length + Object.values(s.secondInvestPurch).filter(d => d).length;
  }, 0);
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const events: CalendarEvent[] = [];
    Object.entries(drafts).forEach(([period, state]) => {
      ;
      (['primaryRefi', 'secondInvestPurch'] as const).forEach(track => {
        ;
        (['initialLE', 'signCD', 'close'] as const).forEach(field => {
          const date = state[track][field];
          if (date) {
            events.push({
              id: `${period}-${track}-${field}`,
              title: MILESTONE_TITLES[track][field],
              date,
              calendar: 'Important Dates',
              track,
              milestone: field,
              period
            });
          }
        });
      });
    });
    events.sort((a, b) => a.date.localeCompare(b.date));
    setCreatedEvents(events);
    setIsSaved(true);
    onSave(drafts, events);
  };
  const trackOrder: {
    key: keyof ImportantDatesState;
    label: string;
    idPrefix: string;
  }[] = [{
    key: 'primaryRefi',
    label: 'Primary Refinance',
    idPrefix: 'pr'
  }, {
    key: 'secondInvestPurch',
    label: '2nd Homes/ Investment Homes/ Purchases',
    idPrefix: 'sip'
  }];
  const fieldOrder: {
    key: keyof ImportantDatesState['primaryRefi'];
    label: string;
  }[] = [{
    key: 'initialLE',
    label: 'Last Day to Send Out Initial LE'
  }, {
    key: 'signCD',
    label: 'Last Day to Get CD Signed'
  }, {
    key: 'close',
    label: 'Last Day to Close'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-sm border border-slate-200" data-id="element-1">
      <div className="mb-6" data-id="element-2">
        <h2 className="text-2xl font-semibold text-slate-900" data-id="element-3">
          Manage Important Dates
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-4">
          Prep deadlines for the current month and any future month, then create
          all calendar events at once.
        </p>
      </div>

      {isSaved ? <motion.div initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-id="element-5">
          <div className="flex items-center gap-3 mb-2 text-emerald-800" data-id="element-6">
            <CheckCircle2 className="w-6 h-6" data-id="element-7" />
            <h3 className="text-lg font-medium" data-id="element-8">
              Created {createdEvents.length} calendar event
              {createdEvents.length === 1 ? '' : 's'}
            </h3>
          </div>
          <p className="text-sm text-emerald-700 mb-6" data-id="element-9">
            Assigned to the “Important Dates” calendar across{' '}
            {preppedMonths.length} month
            {preppedMonths.length === 1 ? '' : 's'}.
          </p>

          {periodOptions.filter(opt => createdEvents.some(e => e.period === opt.key)).map(opt => <div key={opt.key} className="mb-5 last:mb-0" data-id="element-10">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2" data-id="element-11">
                  {opt.label}
                </div>
                <div className="space-y-3" data-id="element-12">
                  {createdEvents.filter(e => e.period === opt.key).map(event => <div key={event.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-4 rounded-lg border border-emerald-100 shadow-sm gap-4" data-id="element-13">
                        <div className="flex items-center gap-3" data-id="element-14">
                          <div className="bg-emerald-100 p-2 rounded-full" data-id="element-15">
                            <Calendar className="w-4 h-4 text-emerald-600" data-id="element-16" />
                          </div>
                          <span className="font-medium text-slate-700" data-id="element-17">
                            {event.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-4" data-id="element-18">
                          <span className="text-sm font-medium text-slate-600" data-id="element-19">
                            {new Date(event.date + 'T00:00:00').toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200" data-id="element-20">
                            {event.calendar}
                          </span>
                        </div>
                      </div>)}
                </div>
              </div>)}

          <div className="mt-6 flex justify-end" data-id="element-21">
            <button onClick={() => setIsSaved(false)} className="text-sm font-medium text-emerald-700 hover:text-emerald-800 px-4 py-2 bg-emerald-100 rounded-lg transition-colors" data-id="element-22">
              Continue Editing
            </button>
          </div>
        </motion.div> : <form onSubmit={handleSave} className="space-y-8" data-id="element-23">
          {/* Month / Year selector */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-24">
            <div className="flex-1" data-id="element-25">
              <label htmlFor="period-select" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-26">
                Editing month
              </label>
              <div className="relative" data-id="element-27">
                <select id="period-select" value={selectedPeriod} onChange={e => {
              setSelectedPeriod(e.target.value);
              setIsSaved(false);
            }} className="w-full appearance-none px-3 py-2 pr-10 border border-slate-300 rounded-md shadow-sm bg-white focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-slate-900" data-id="element-28">
                  {periodOptions.map(opt => <option key={opt.key} value={opt.key} data-id="element-29">
                      {opt.label}
                      {hasDates(drafts[opt.key] ?? emptyDatesState) ? ' • prepped' : ''}
                    </option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-30" />
              </div>
            </div>
            <p className="text-xs text-slate-500 sm:max-w-[16rem]" data-id="element-31">
              Switch months to prep future deadlines. All months you fill in are
              saved together.
            </p>
          </div>

          {/* Prepped months summary */}
          {preppedMonths.length > 0 && <div className="flex flex-wrap items-center gap-2" data-id="element-32">
              <span className="text-xs font-medium text-slate-500" data-id="element-33">
                {totalDateCount} date{totalDateCount === 1 ? '' : 's'} across:
              </span>
              {preppedMonths.map(key => <button type="button" key={key} onClick={() => setSelectedPeriod(key)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${key === selectedPeriod ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-34">
                  <CircleDot className="w-3 h-3" data-id="element-35" />
                  {periodLabel(key)}
                </button>)}
            </div>}

          {trackOrder.map(track => <div key={track.key} className="space-y-4" data-id="element-36">
              <h3 className="text-lg font-medium text-slate-800 border-b border-slate-100 pb-2" data-id="element-37">
                {track.label}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-38">
                {fieldOrder.map(field => {
            const id = `${track.idPrefix}-${field.key}`;
            return <div key={id} className="space-y-2" data-id="element-39">
                      <label htmlFor={id} className="block text-sm font-medium text-slate-700" data-id="element-40">
                        {field.label}
                      </label>
                      <input type="date" id={id} className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-slate-900" value={currentState[track.key][field.key]} onChange={e => handleDateChange(track.key, field.key, e.target.value)} data-id="element-41" />
                    </div>;
          })}
              </div>
            </div>)}

          <div className="pt-6 flex items-center justify-end border-t border-slate-100" data-id="element-42">
            {preppedMonths.length === 0 && <div className="flex items-center text-amber-600 text-sm mr-4" data-id="element-43">
                <AlertCircle className="w-4 h-4 mr-1.5" data-id="element-44" />
                Please enter at least one date
              </div>}
            <button type="submit" disabled={preppedMonths.length === 0} className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-45">
              Save &amp; Create Calendar Events
            </button>
          </div>
        </form>}
    </motion.div>;
}